import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageSquare, AlertTriangle, AlertCircle, Info, 
  X, ChevronLeft, ChevronRight, Clock, User, Check, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const MessagePopup = ({ onClose }) => {
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  
  // Direct messages state
  const [directMessages, setDirectMessages] = useState([]);
  const [dmIndex, setDmIndex] = useState(0);
  
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('announcements');

  useEffect(() => {
    fetchAllUnread();
  }, []);

  const fetchAllUnread = async () => {
    try {
      setLoading(true);
      
      // Fetch unread announcements
      const announcementsRes = await axios.get(`${API}/messages/unread`, getAuthHeader());
      const unreadAnnouncements = announcementsRes.data || [];
      setAnnouncements(unreadAnnouncements);
      
      // Fetch unread direct messages
      const dmRes = await axios.get(`${API}/direct-messages/unread`, getAuthHeader());
      const unreadDms = dmRes.data || [];
      setDirectMessages(unreadDms);
      
      // Show popup if there are any unread messages
      if (unreadAnnouncements.length > 0 || unreadDms.length > 0) {
        setOpen(true);
        // Default to the tab that has messages
        if (unreadAnnouncements.length === 0 && unreadDms.length > 0) {
          setActiveTab('direct');
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Announcement functions
  const dismissAnnouncement = async (messageId) => {
    try {
      await axios.post(`${API}/messages/${messageId}/dismiss`, {}, getAuthHeader());
      
      const newAnnouncements = announcements.filter(m => m.id !== messageId);
      setAnnouncements(newAnnouncements);
      
      if (newAnnouncements.length === 0) {
        if (directMessages.length > 0) {
          setActiveTab('direct');
        } else {
          handleClose();
        }
      } else if (announcementIndex >= newAnnouncements.length) {
        setAnnouncementIndex(newAnnouncements.length - 1);
      }
    } catch (error) {
      console.error('Failed to dismiss message:', error);
    }
  };

  const dismissAllAnnouncements = async () => {
    try {
      await Promise.all(announcements.map(m => 
        axios.post(`${API}/messages/${m.id}/dismiss`, {}, getAuthHeader())
      ));
      setAnnouncements([]);
      if (directMessages.length > 0) {
        setActiveTab('direct');
      } else {
        handleClose();
      }
    } catch (error) {
      console.error('Failed to dismiss all messages:', error);
    }
  };

  // Direct message functions
  const markDmAsRead = async (conversationId) => {
    try {
      // Opening the conversation marks messages as read
      await axios.get(`${API}/direct-messages/conversations/${conversationId}`, getAuthHeader());
      
      const newDms = directMessages.filter(m => m.conversation_id !== conversationId);
      setDirectMessages(newDms);
      
      if (newDms.length === 0) {
        if (announcements.length > 0) {
          setActiveTab('announcements');
        } else {
          handleClose();
        }
      } else if (dmIndex >= newDms.length) {
        setDmIndex(newDms.length - 1);
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllDmsAsRead = async () => {
    try {
      await Promise.all(directMessages.map(m => 
        axios.get(`${API}/direct-messages/conversations/${m.conversation_id}`, getAuthHeader())
      ));
      setDirectMessages([]);
      if (announcements.length > 0) {
        setActiveTab('announcements');
      } else {
        handleClose();
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <AlertTriangle className="w-6 h-6 text-red-500" />;
      case 'important':
        return <AlertCircle className="w-6 h-6 text-amber-500" />;
      default:
        return <Info className="w-6 h-6 text-blue-500" />;
    }
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'border-red-500 bg-red-50';
      case 'important':
        return 'border-amber-500 bg-amber-50';
      default:
        return 'border-blue-500 bg-blue-50';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || (announcements.length === 0 && directMessages.length === 0)) {
    return null;
  }

  const currentAnnouncement = announcements[announcementIndex];
  const currentDm = directMessages[dmIndex];
  const totalUnread = announcements.length + directMessages.length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="message-popup">
        {/* Header */}
        <div className="p-4 border-b bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-emerald-600" />
              <div>
                <h2 className="font-bold text-lg text-slate-900">New Messages</h2>
                <p className="text-sm text-slate-600">
                  {totalUnread} unread message{totalUnread !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose} data-testid="close-popup">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Tabs for different message types */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full rounded-none border-b">
            <TabsTrigger value="announcements" className="flex-1" disabled={announcements.length === 0}>
              <MessageSquare className="w-4 h-4 mr-2" />
              Announcements
              {announcements.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-blue-500 text-white rounded-full">
                  {announcements.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="direct" className="flex-1" disabled={directMessages.length === 0}>
              <Mail className="w-4 h-4 mr-2" />
              Direct Messages
              {directMessages.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-emerald-500 text-white rounded-full">
                  {directMessages.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="mt-0">
            {currentAnnouncement && (
              <>
                <div className={`p-3 border-b-2 ${getPriorityStyles(currentAnnouncement.priority)}`}>
                  <div className="flex items-center gap-2">
                    {getPriorityIcon(currentAnnouncement.priority)}
                    <span className="text-sm font-medium">
                      {currentAnnouncement.priority.charAt(0).toUpperCase() + currentAnnouncement.priority.slice(1)} Priority
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-semibold text-xl text-slate-900 mb-3" data-testid="announcement-title">
                    {currentAnnouncement.title}
                  </h3>
                  
                  <div className="bg-slate-50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto">
                    <p className="text-slate-700 whitespace-pre-wrap" data-testid="announcement-content">
                      {currentAnnouncement.content}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {currentAnnouncement.created_by_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDate(currentAnnouncement.created_at)}
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {announcements.length > 1 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAnnouncementIndex(Math.max(0, announcementIndex - 1))}
                          disabled={announcementIndex === 0}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-slate-500 px-2">
                          {announcementIndex + 1} / {announcements.length}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAnnouncementIndex(Math.min(announcements.length - 1, announcementIndex + 1))}
                          disabled={announcementIndex === announcements.length - 1}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {announcements.length > 1 && (
                      <Button variant="outline" size="sm" onClick={dismissAllAnnouncements}>
                        Dismiss All
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => dismissAnnouncement(currentAnnouncement.id)}
                      data-testid="mark-announcement-read"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Mark as Read
                    </Button>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Direct Messages Tab */}
          <TabsContent value="direct" className="mt-0">
            {currentDm && (
              <>
                <div className="p-3 border-b-2 border-emerald-500 bg-emerald-50">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700">
                      From: {currentDm.from_name}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-semibold text-xl text-slate-900 mb-1" data-testid="dm-subject">
                    {currentDm.conversation_subject}
                  </h3>
                  <p className="text-sm text-slate-500 mb-3">
                    Direct message from {currentDm.from_name}
                  </p>
                  
                  <div className="bg-slate-50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto">
                    <p className="text-slate-700 whitespace-pre-wrap" data-testid="dm-content">
                      {currentDm.content}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDate(currentDm.created_at)}
                    </span>
                  </div>

                  <p className="text-sm text-emerald-600 mt-3">
                    Go to Messages tab to view full conversation and reply.
                  </p>
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {directMessages.length > 1 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDmIndex(Math.max(0, dmIndex - 1))}
                          disabled={dmIndex === 0}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-slate-500 px-2">
                          {dmIndex + 1} / {directMessages.length}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDmIndex(Math.min(directMessages.length - 1, dmIndex + 1))}
                          disabled={dmIndex === directMessages.length - 1}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {directMessages.length > 1 && (
                      <Button variant="outline" size="sm" onClick={markAllDmsAsRead}>
                        Mark All Read
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => markDmAsRead(currentDm.conversation_id)}
                      data-testid="mark-dm-read"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Mark as Read
                    </Button>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default MessagePopup;
